import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { Repeat } from 'lucide-react'
import { icons } from '../../styles/images'
import { TRecurringTaskTemplate } from '../../utils/types'
import { Icon } from '../atoms/Icon'

export {
    activeRecurringTemplates,
    addGoalToTemplateHook,
    removeGoalFromTemplateHook,
    toggleHookSelection,
} from './recurringGoalHookUtils'

interface HookableItem {
    id: string
    title: string
}

interface GoalHookCheckboxListProps<T extends HookableItem> {
    items: T[]
    selectedIds: string[]
    onToggle: (id: string) => void
    maxItems?: number
    showRecurringIcon?: boolean
    rowClassName?: string
    titleClassName?: string
}

export const GoalHookCheckboxList = <T extends HookableItem>({
    items,
    selectedIds,
    onToggle,
    maxItems,
    showRecurringIcon = false,
    rowClassName,
    titleClassName,
}: GoalHookCheckboxListProps<T>) => {
    const visibleItems = maxItems ? items.slice(0, maxItems) : items
    return (
        <div className="space-y-0.5">
            {visibleItems.map((item) => (
                <label
                    key={item.id}
                    className={cn(
                        'flex min-w-0 cursor-pointer items-center gap-3 rounded-lg px-1 py-1.5 text-foreground',
                        rowClassName
                    )}
                >
                    <Checkbox
                        checked={selectedIds.includes(item.id)}
                        onCheckedChange={() => onToggle(item.id)}
                        aria-label={item.title}
                    />
                    {showRecurringIcon && <Repeat className="size-4 shrink-0 text-muted-foreground" />}
                    <span className={cn('min-w-0 flex-1 truncate text-body-md', titleClassName)}>{item.title}</span>
                </label>
            ))}
        </div>
    )
}

interface RecurringHookPopoverProps {
    candidates: TRecurringTaskTemplate[]
    onPick: (template: TRecurringTaskTemplate) => void
}

export const RecurringHookPopover = ({ candidates, onPick }: RecurringHookPopoverProps) => {
    const [open, setOpen] = useState(false)
    const [query, setQuery] = useState('')
    const q = query.trim().toLowerCase()
    const matches = candidates.filter((t) => !q || t.title.toLowerCase().includes(q)).slice(0, 8)
    return (
        <Popover
            open={open}
            onOpenChange={(next) => {
                setOpen(next)
                if (!next) setQuery('')
            }}
        >
            <PopoverTrigger asChild>
                <Button variant="ghost" size="sm" className="h-7 gap-1.5 px-2 text-muted-foreground">
                    <Icon icon={icons.arrows_repeat} color="gray" size="small" />
                    Hook recurring
                </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-72 p-1">
                <Input
                    autoFocus
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search recurring..."
                    className="mb-1 h-8"
                />
                {matches.length === 0 ? (
                    <p className="px-2 py-2 text-body-sm text-muted-foreground">No recurring tasks match.</p>
                ) : (
                    matches.map((template) => (
                        <button
                            key={template.id}
                            type="button"
                            onClick={() => {
                                onPick(template)
                                setOpen(false)
                                setQuery('')
                            }}
                            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-body-sm text-foreground transition-colors hover:bg-muted"
                        >
                            <Icon icon={icons.arrows_repeat} color="gray" size="small" />
                            <span className="min-w-0 flex-1 truncate">{template.title}</span>
                        </button>
                    ))
                )}
            </PopoverContent>
        </Popover>
    )
}
